'use client';

import { useState } from 'react';
import { Trans } from 'react-i18next';

import { useTranslation, i18n } from '@lichtblick/i18next';
import { NotInterested, Check } from '@lichtblick/icons';
import { Colors, TextVariants } from '@lichtblick/theme';
import { ApprovalState } from '@lichtblick/types';

import {
  ButtonContainer,
  CustomApproveDialog,
  CustomRejectDialog,
  DialogContainer,
  ImageGrid,
  ImageContainer,
  PreviewImage,
} from './FieldSection.styles';

import { Button } from '../../Button';
import { Text } from '../../Text';
import { Textarea } from '../../Textarea';
export type FieldSectionProps = {
  deselectedImages?: string[];
  hasDeselectedImages?: boolean;
  isAssignedQm?: boolean;
  isMargin?: boolean;
  onSubmit: (decision: ApprovalState, rejectReason?: string) => void;
};

export const FieldSection = ({
  deselectedImages = [],
  hasDeselectedImages = false,
  isAssignedQm = true,
  isMargin = true,
  onSubmit,
}: FieldSectionProps) => {
  const { t } = useTranslation('common');
  const [reason, setReason] = useState('');
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState<boolean>(false);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState<boolean>(false);

  const handleSubmit = (decision: ApprovalState, rejectReason?: string) => {
    onSubmit(decision, rejectReason);
    setReason('');
  };

  return (
    <>
      <ButtonContainer $isMargin={isMargin}>
        <CustomRejectDialog
          isActionContainer={false}
          isOpen={isRejectionModalOpen}
          onClose={() => setIsRejectionModalOpen(false)}
          trigger={
            <Button
              icon={<NotInterested />}
              isDisabled={!isAssignedQm}
              label={t('reject')}
              onClick={() => setIsRejectionModalOpen(true)}
              variant="rejection"
            />
          }
        >
          <DialogContainer>
            {hasDeselectedImages ? (
              <>
                <Text $variant={TextVariants.header.s}>
                  <Trans
                    components={[<Text $variant={TextVariants.header.s} color={Colors.red[100]} key="label"></Text>]}
                    i18n={i18n}
                    i18nKey="ui.chat.rejectImagesTitle"
                  />
                </Text>
                <Text $variant={TextVariants.body.s}>{t('ui.chat.rejectImagesSubtitle')}</Text>
                <Text $variant={TextVariants.title.sMedium}>{t('ui.chat.rejectingImages')}</Text>
                <ImageGrid>
                  {deselectedImages.map((imageUrl, index) => (
                    <ImageContainer key={imageUrl}>
                      <PreviewImage alt={`Abzulehnendes Bild ${index + 1}`} src={imageUrl} />
                    </ImageContainer>
                  ))}
                </ImageGrid>
              </>
            ) : (
              <Text $variant={TextVariants.header.s}>
                <Trans
                  components={[<Text $variant={TextVariants.header.s} color={Colors.red[100]} key="label"></Text>]}
                  i18n={i18n}
                  i18nKey="ui.chat.rejectTip"
                />
              </Text>
            )}
            <Text $variant={TextVariants.body.s}>{t('ui.chat.note')}</Text>
            <Textarea
              maxLength={600}
              onChange={(event) => setReason(event.target.value)}
              placeholder={hasDeselectedImages ? 'Kommentar (max. 600)' : t('maxComment600')}
              value={reason}
            />
            <Button
              icon={<NotInterested />}
              isDisabled={(hasDeselectedImages && !reason.length) || (!hasDeselectedImages && !reason.length)}
              label={hasDeselectedImages ? 'Ablehnen' : t('reject')}
              onClick={() => handleSubmit(ApprovalState.Rejected, reason)}
              variant="rejection"
            />
          </DialogContainer>
        </CustomRejectDialog>
        <CustomApproveDialog
          isActionContainer={false}
          isOpen={isApprovalModalOpen}
          onClose={() => setIsApprovalModalOpen(false)}
          trigger={
            <Button
              icon={<Check />}
              isDisabled={hasDeselectedImages || !isAssignedQm}
              label={t('approve')}
              onClick={() => setIsApprovalModalOpen(true)}
              variant="approval"
            />
          }
        >
          <DialogContainer>
            <Text $variant={TextVariants.header.s} key="tip">
              <Trans
                components={[<Text $variant={TextVariants.header.s} color={Colors.green[100]} key="label"></Text>]}
                i18n={i18n}
                i18nKey="ui.chat.approveTip"
              />
            </Text>
            <Textarea
              maxLength={600}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('maxComment600')}
              value={reason}
            />
            <Button
              icon={<Check />}
              isDisabled={hasDeselectedImages}
              label={t('approve')}
              onClick={() => handleSubmit(ApprovalState.ServerApproved, reason)}
              variant="approval"
            />
          </DialogContainer>
        </CustomApproveDialog>
      </ButtonContainer>
    </>
  );
};
